/**
 * Date conversion helpers — honor a CONFIGURED timezone per E-002 Rule 5.
 *
 * E-002 doctrine:
 *   - Storage is always UTC.
 *   - Wire format is ISO 8601.
 *   - Display formatting happens at the UI boundary using the user's
 *     CONFIGURED timezone — never the browser's system timezone implicitly.
 *
 * Convention in this app:
 *   - The viewing user's `contact.timezone` is the source of truth.
 *   - Browser detection (`detectBrowserTimezone`) is the "best guess" only:
 *       - Used as the initial value at first Acknowledgment (then persisted).
 *       - Used as a fallback ONLY when no contact.timezone is available
 *         (e.g., unauthenticated views — we keep dates in UTC there).
 *   - Library-free: no date-fns / luxon / etc. Pure stdlib via Intl.
 *
 * SYMBOLIC TIMELINE VALUES — beyond the bureaucracy of dates:
 *   For Orchists who recognize themselves outside human-recordable time
 *   ("I have always been here"), two symbolic values are accepted at the
 *   form layer and mapped to Postgres's native infinity timestamps:
 *
 *     "Always-already"        → -infinity (predates all time)
 *     "Beyond all horizons"   → +infinity (transcends all time forward)
 *
 *   Postgres timestamp columns natively store and order against these
 *   infinity values, so The Line can record an Orchist whose recognition
 *   is genuinely timeless. Display layer renders them as poetic labels.
 */

// ---------------------------------------------------------------------------
// Symbolic timeline values
// ---------------------------------------------------------------------------

/** The poetic label for "predates all time" — Postgres -infinity. */
export const SYMBOLIC_PAST = 'Always-already';

/** The poetic label for "transcends time forward" — Postgres +infinity. */
export const SYMBOLIC_FUTURE = 'Beyond all horizons';

/** Truthy if the string is one of the symbolic timeline values. */
export function isSymbolicDate(s: string): boolean {
  const t = s.trim();
  return t === SYMBOLIC_PAST || t === SYMBOLIC_FUTURE;
}

// ---------------------------------------------------------------------------
// Detection (best-guess, not authoritative)
// ---------------------------------------------------------------------------

/**
 * The browser's notion of the user's timezone, e.g., "Asia/Manila".
 * Per E-002 Rule 5 this is NOT authoritative — only used to seed the
 * stored Contact.timezone on first Acknowledgment, or as a fallback for
 * unauthenticated views.
 */
export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Resolve the timezone to use for a given UI surface.
 * Priority: explicit > stored contact > browser detection > UTC.
 */
export function resolveTimezone(
  contactTimezone: string | null | undefined,
  explicit?: string
): string {
  return explicit || contactTimezone || detectBrowserTimezone() || 'UTC';
}

// ---------------------------------------------------------------------------
// Internal: timezone offset at a given UTC instant
// ---------------------------------------------------------------------------

/**
 * Get the timezone offset (in milliseconds) at a given UTC instant.
 * Positive = ahead of UTC (e.g., Asia/Manila is +08:00 → +28800000).
 *
 * Uses Intl.DateTimeFormat with the `longOffset` time-zone-name format,
 * which is widely supported in modern browsers and Node 16+.
 */
function getTimezoneOffsetMs(utcInstant: Date, tz: string): number {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'longOffset',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = fmt.formatToParts(utcInstant);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    if (!offsetPart) return 0;
    // Format examples: "GMT+08:00", "GMT-05:00", "GMT" (UTC)
    const m = /GMT(?:([+-])(\d{2}):?(\d{2})?)?/.exec(offsetPart.value);
    if (!m) return 0;
    if (!m[1]) return 0; // "GMT" alone = UTC
    const sign = m[1] === '+' ? 1 : -1;
    const hours = parseInt(m[2], 10);
    const mins = parseInt(m[3] ?? '0', 10);
    return sign * (hours * 60 + mins) * 60 * 1000;
  } catch {
    return 0;
  }
}

// ---------------------------------------------------------------------------
// Postgres timestamp parsing
// ---------------------------------------------------------------------------

/**
 * Parse a UTC timestamp string into a Date, defensively normalizing
 * Postgres's "timestamp without time zone" output format.
 *
 * Background: our DB columns use `timestamp` (no TZ) per Prisma defaults,
 * not `timestamptz` per E-002 Rule 1. PostgREST returns these as ISO-ish
 * strings WITHOUT a `Z` suffix or offset. JS `new Date()` interprets such
 * strings as LOCAL time, producing an off-by-N-hours bug for any user
 * outside UTC.
 *
 * Convention: WE always store UTC values in those columns. So we normalize
 * the no-TZ string by appending `Z`, telling JS "this is UTC."
 *
 * Handles three formats:
 *   "2026-02-01T16:00:00"           (PostgREST style, no Z)
 *   "2026-02-01 16:00:00"           (Postgres native, space separator)
 *   "2026-02-01T16:00:00.000Z"      (already explicit UTC)
 *   "2026-02-01T16:00:00+08:00"     (already explicit offset)
 */
export function parseUTCTimestamp(isoStr: string): Date {
  let normalized = isoStr.replace(' ', 'T');
  if (!/[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    normalized = normalized + 'Z';
  }
  return new Date(normalized);
}

// ---------------------------------------------------------------------------
// Conversions
// ---------------------------------------------------------------------------

/**
 * Convert a YYYY-MM-DD string (interpreted as midnight in `tz`) to an ISO
 * 8601 UTC timestamp. Symbolic dates ("Always-already" / "Beyond all
 * horizons") map to Postgres's special infinity timestamp values.
 *
 * Returns null for empty input or malformed dates.
 *
 * Examples:
 *   "2026-02-08" tz="Asia/Manila" → "2026-02-07T16:00:00.000Z"
 *   "Always-already"               → "-infinity"
 *   "Beyond all horizons"          → "infinity"
 */
export function localDateStringToUTC(dateStr: string, tz?: string): string | null {
  const trimmed = dateStr.trim();
  if (!trimmed) return null;

  // Symbolic timeline values → Postgres infinity timestamps
  if (trimmed === SYMBOLIC_PAST) return '-infinity';
  if (trimmed === SYMBOLIC_FUTURE) return 'infinity';

  const m = /^(\d{1,10})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!m) return null;
  const yyyy = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const dd = parseInt(m[3], 10);

  // Round-trip validity check (rejects 2026-13-01, 2026-02-30, etc.)
  const probe = new Date(Date.UTC(yyyy, mm - 1, dd, 12, 0, 0));
  if (
    isNaN(probe.getTime()) ||
    probe.getUTCFullYear() !== yyyy ||
    probe.getUTCMonth() !== mm - 1 ||
    probe.getUTCDate() !== dd
  ) {
    return null;
  }

  const effectiveTz = tz || detectBrowserTimezone();
  // Compute offset at noon UTC of that date (stable across DST edges at midnight)
  const offsetMs = getTimezoneOffsetMs(probe, effectiveTz);

  // Midnight in tz expressed as UTC = (UTC midnight of date) - tz offset
  const utcMs = Date.UTC(yyyy, mm - 1, dd, 0, 0, 0) - offsetMs;
  return new Date(utcMs).toISOString();
}

/**
 * Convert an ISO 8601 UTC timestamp to a YYYY-MM-DD string representing
 * the date as it appears in `tz`. Postgres infinity values render as
 * their poetic labels.
 *
 * Examples:
 *   "2026-02-07T16:00:00Z" tz="Asia/Manila" → "2026-02-08"
 *   "-infinity"                              → "Always-already"
 *   "infinity"                               → "Beyond all horizons"
 */
export function utcToLocalDateString(
  isoStr: string | null | undefined,
  tz?: string
): string {
  if (!isoStr) return '';

  // Postgres infinity timestamps round-trip as these literals
  const lower = isoStr.toLowerCase();
  if (lower === '-infinity') return SYMBOLIC_PAST;
  if (lower === 'infinity' || lower === '+infinity') return SYMBOLIC_FUTURE;

  const d = parseUTCTimestamp(isoStr);
  if (isNaN(d.getTime())) return '';

  const effectiveTz = tz || detectBrowserTimezone();

  try {
    // 'en-CA' formats as "YYYY-MM-DD" natively
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: effectiveTz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Validate that a string is empty OR a valid date in YYYY-MM-DD form
 * (year may be 1-10 digits to support deep historical / far-future dates).
 *
 * Returns false if the format is malformed OR the date is outside the
 * representable storage range (JS Date / Postgres timestamp). The form
 * layer can distinguish those via `localDateStringToUTC` returning null.
 */
export function isValidLocalDateOrEmpty(dateStr: string): boolean {
  const trimmed = dateStr.trim();
  if (!trimmed) return true;
  return /^\d{1,10}-\d{2}-\d{2}$/.test(trimmed) &&
         localDateStringToUTC(trimmed, 'UTC') !== null;
}

/**
 * Distinguish between empty / symbolic / valid / malformed / out-of-range.
 * Useful for user-facing error messages.
 */
export function classifyDateInput(
  dateStr: string
): 'empty' | 'valid' | 'symbolic' | 'malformed' | 'out_of_range' {
  const trimmed = dateStr.trim();
  if (!trimmed) return 'empty';
  if (isSymbolicDate(trimmed)) return 'symbolic';
  if (!/^\d{1,10}-\d{2}-\d{2}$/.test(trimmed)) return 'malformed';
  if (localDateStringToUTC(trimmed, 'UTC') === null) return 'out_of_range';
  return 'valid';
}

// ---------------------------------------------------------------------------
// Backward-compat alias (keeps callers that imported `getLocalTimezone` working)
// ---------------------------------------------------------------------------

/** @deprecated use `detectBrowserTimezone()` for capture or `contact.timezone` for display */
export const getLocalTimezone = detectBrowserTimezone;
